import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ariaProps, dataProps, onProps } from '@ds/react-utils'
import { removeStyleProps } from '../../utilities'
import { CustomPropTypes } from '../../support'
import { useThemeStyles } from '../../theming'
import type { InputForwardRef, DOMRef } from '../../types'
import styles from './styles'
import { useFormContext } from '../FormContext/FormContext'

export type FileInputProps = 'accept' | 'capture' | 'multiple'

export interface InputTextBoxProps
  extends Omit<
    React.ComponentPropsWithoutRef<'input'>,
    'children' | 'width' | FileInputProps
  > {
  /**
   * The unique identifier aria uses to associate the control with a description.
   */
  ariaDescribedById?: string
  /**
   * A React ref to receive the container element, which wraps the input and start/end elements. Can be used to anchor other elements, such as a Menu or Tooltip.
   */
  containerRef?: DOMRef<HTMLDivElement>
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * The element to display at the end of the InputTextBox.
   */
  endElement?: React.ReactNode
  /**
   * A React ref to receive the input element. Useful for setting focus. If you need an anchor see `containerRef`.
   */
  forwardedRef?: InputForwardRef
  /**
   * An invalid state on one of the children form elements.
   */
  invalid?: boolean
  /**
   * The element to display at the start of the InputTextBox.
   */
  startElement?: React.ReactNode
  /**
   * The width of the InputTextBox.
   */
  width?: string
}

export function InputTextBox({
  ariaDescribedById,
  'aria-invalid': ariaInvalid,
  autoCapitalize,
  autoComplete,
  containerRef,
  disabled = false,
  endElement,
  forwardedRef,
  invalid = false,
  onBlur,
  onFocus,
  readOnly = false,
  required = false,
  startElement,
  type = 'text',
  width,
  'data-qa': dataQa,
  ...restProps
}: InputTextBoxProps): JSX.Element {
  const [inFocus, setInFocus] = useState(false)

  const formContext = useFormContext()

  const sx = useThemeStyles(styles, {
    startElement,
    endElement,
  })

  const inputTextBoxBaseStyles = [
    sx.default.input,
    type === 'number' && sx.hideNumberPickerSpinner.input,
  ]

  const wrapperStyles = [
    sx.default.wrapper,
    invalid && sx.invalid.wrapper,
    disabled && sx.disabled.wrapper,
    readOnly && sx.readOnly.wrapper,
    inFocus && sx.focus.wrapper,
    width && { width },
  ]

  const autoCapitalizeValue =
    autoCapitalize || (type === 'email' ? 'off' : undefined)
  const autoCompleteValue =
    autoComplete || (type === 'email' ? 'email' : undefined)

  return (
    <div
      className="input-text-box-wrapper"
      css={wrapperStyles}
      data-qa={dataQa && `${dataQa}-wrapper`}
      ref={containerRef}
    >
      {startElement}
      <input
        {...ariaProps(restProps)}
        {...dataProps(restProps)}
        {...onProps(restProps)}
        {...removeStyleProps(restProps)}
        aria-describedby={ariaDescribedById}
        aria-invalid={invalid ? true : ariaInvalid}
        aria-required={
          formContext?.requiredAttribute ? undefined : required || undefined
        }
        autoCapitalize={autoCapitalizeValue}
        autoComplete={autoCompleteValue}
        css={inputTextBoxBaseStyles}
        data-qa={dataQa}
        disabled={disabled}
        onBlur={(event) => {
          setInFocus(false)
          onBlur?.(event)
        }}
        onFocus={(event) => {
          setInFocus(true)
          onFocus?.(event)
        }}
        readOnly={readOnly}
        ref={forwardedRef}
        required={formContext?.requiredAttribute ? required : undefined}
        type={type}
      />
      {endElement}
    </div>
  )
}

InputTextBox.propTypes = {
  ariaDescribedById: PropTypes.string,
  containerRef: CustomPropTypes.ReactRef,
  'data-.*': PropTypes.string,
  endElement: PropTypes.element,
  forwardedRef: CustomPropTypes.ReactRef,
  invalid: PropTypes.bool,
  startElement: PropTypes.element,
  width: PropTypes.string,
}

InputTextBox.displayName = 'InputTextBox'
